import { useEffect, useLayoutEffect, useState, type ReactNode } from 'react';

// In-app help: a "?" tip on each step, and a short first-time tour of Create lot.
// The wording follows the printed Lister Work Instructions (/lister-guide.pdf).

export const GUIDE_PDF = '/lister-guide.pdf';

export const TIPS = {
  label: (
    <ul>
      <li>Last step: stick a lot label on the item, then scan <strong>that</strong> label.</li>
      <li>Not the product's barcode (step 1), and not a BIN label (step 4).</li>
      <li>A label is used once, forever. Never peel one off and reuse it.</li>
    </ul>
  ),
  product: (
    <ul>
      <li>Scan the product barcode, or search by item number or words.</li>
      <li>Check the photo and title match the item in your hands. Wrong? <strong>Change product</strong>.</li>
      <li>
        <strong>The 50% rule:</strong> everything there → <em>Yes, complete</em>. More than half there → <em>No, pieces missing</em>. Half or less, or pieces from
        different items → <em>Mixed / parts lot</em>.
      </li>
    </ul>
  ),
  details: (
    <ul>
      <li>Facts only: what you can see or checked. If you don't know something, leave it blank.</li>
      <li>Lot inspection: opened or sealed, tested or not, damage, marks.</li>
      <li>Pieces missing: the title and Costco description describe the complete item, so you must change both (use the buttons).</li>
    </ul>
  ),
  photos: (
    <ul>
      <li>Shoot the whole item, the label or model number, and every defect up close.</li>
      <li>Incomplete or mixed lot: lay out everything that's there in one photo.</li>
      <li>Untick the Costco photo if it doesn't match what you're selling.</li>
    </ul>
  ),
  storage: (
    <ul>
      <li>Scan the shelf label (location) or the BIN label.</li>
      <li>A lot in a bin is always wherever the bin is.</li>
      <li>No spot yet? Save with no location, then use <strong>Move</strong> once it's put away.</li>
    </ul>
  ),
  move: (
    <ul>
      <li>1: scan where things are going (a shelf or a BIN label).</li>
      <li>2: scan each lot label. Scanning a BIN label moves the whole bin and everything in it.</li>
      <li>A lot moved to a shelf on its own comes out of its bin.</li>
    </ul>
  ),
  bins: (
    <ul>
      <li>Open a bin by scanning its BIN label, then scan lots into it.</li>
      <li>Print the packing list and put it in the bin before it ships.</li>
      <li>When the bin moves, move the bin (not each lot): its lots go with it.</li>
    </ul>
  ),
} satisfies Record<string, ReactNode>;

/** A step heading with a "?" that opens its tip underneath. */
export function StepHeading({ tip, children }: { tip: keyof typeof TIPS; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="step-head">
        <h2>{children}</h2>
        <button type="button" className={`tip-btn ${open ? 'on' : ''}`} aria-expanded={open} aria-label="Tips for this step" onClick={() => setOpen(!open)}>
          ?
        </button>
      </div>
      {open && <div className="tip">{TIPS[tip]}</div>}
    </>
  );
}

export interface TourStep {
  /** The `data-tour` value of the element to highlight; none = a card on its own. */
  target?: string;
  /** Show the card at the top of the screen (for targets at the bottom, like the save bar). */
  cardAtTop?: boolean;
  title: string;
  body: ReactNode;
}

export const CREATE_LOT_TOUR: TourStep[] = [
  {
    title: 'Listing a lot: a quick tour',
    body: <p>Every lot goes through the same 5 steps, top to bottom. This takes a minute. You can skip it and replay it from Home → How to list.</p>,
  },
  {
    target: 'product',
    title: '1. Product',
    body: (
      <>
        <p>Scan the product barcode or search. Check the photo matches the item in your hands.</p>
        <p>
          Then: <strong>Yes, complete</strong> · <strong>No, pieces missing</strong> (more than half there) · or <strong>Mixed / parts lot</strong> (half or less, or
          pieces from different items).
        </p>
      </>
    ),
  },
  { target: 'photos', title: '2. Photos', body: <p>Whole item, labels, every defect. The Costco photo goes first when it matches.</p> },
  {
    target: 'details',
    title: '3. Details',
    body: <p>Title, condition, what you checked. Facts only; blank is fine when you don't know. Amber boxes must be changed before you can save.</p>,
  },
  { target: 'storage', title: '4. Storage', body: <p>Scan the shelf or BIN label. The next lot keeps the same spot, so you only scan it when it changes.</p> },
  { target: 'label', title: '5. Lot label', body: <p>Last: stick a lot label on the item and scan it here. Only lot labels: not product barcodes, not BIN labels.</p> },
  {
    target: 'save',
    cardAtTop: true,
    title: 'Save',
    body: <p>If Save is greyed out, the red lines above it say what's left. The app keeps your unsaved lot if the phone locks.</p>,
  },
  {
    title: "That's it",
    body: (
      <p>
        Tap <strong>?</strong> next to any step for tips. The full instructions are on Home → <strong>How to list</strong>, or{' '}
        <a href={GUIDE_PDF} target="_blank" rel="noreferrer">
          open the printable guide (PDF)
        </a>
        .
      </p>
    ),
  },
];

/** Highlights each step's element in turn, with a card to read and Back / Next / Skip. */
export function Tour({ steps, onClose }: { steps: TourStep[]; onClose: () => void }) {
  const [i, setI] = useState(0);
  const step = steps[i];
  const last = i === steps.length - 1;

  useLayoutEffect(() => {
    const el = step.target ? document.querySelector<HTMLElement>(`[data-tour="${step.target}"]`) : null;
    if (!el) return;
    el.classList.add('tour-focus');
    el.scrollIntoView({ block: 'start', behavior: 'smooth' });
    return () => el.classList.remove('tour-focus');
  }, [step]);
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);

  return (
    <>
      {!step.target && <div className="tour-backdrop" />}
      <div className={`tour-card ${step.cardAtTop ? 'at-top' : ''}`} role="dialog" aria-label={step.title}>
        <div className="muted small">
          {i + 1} of {steps.length}
        </div>
        <h3>{step.title}</h3>
        {step.body}
        <div className="row gap">
          {i > 0 && (
            <button type="button" className="btn btn-small btn-ghost" onClick={() => setI(i - 1)}>
              Back
            </button>
          )}
          <button type="button" className="btn btn-small btn-primary" onClick={() => (last ? onClose() : setI(i + 1))}>
            {last ? 'Done' : 'Next'}
          </button>
          {!last && (
            <button type="button" className="btn btn-small btn-ghost" onClick={onClose}>
              Skip tour
            </button>
          )}
        </div>
      </div>
    </>
  );
}
